import platform from '@play-co/gcinstant';
import { MovieClip } from '@play-co/timestep-core/ui';
import { trackPetDie, trackPetSpawn } from 'src/lib/analytics/events';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import sounds from 'src/lib/sounds';
import statePromise from 'src/lib/statePromise';
import { getCurrentScene } from 'src/lib/stateUtils';
import {
  getCreativeSuffix,
  getSceneManager,
  waitForAssets,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import {
  accessionAssets,
  petCrateAssets,
  petSpeciesAssets,
} from 'src/loadingGroups';
import { Pet } from 'src/replicant/buruburu/pet';
import { setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { careActions } from '../care/care.redux';
import { getPet } from '../pet';

/**
 * Death sequence
 */
export async function ascensionSequence() {
  await statePromise(
    ({ conversation }) => conversation[platform.playerID].activeDialog === null,
  );

  // Play ascension sound
  await waitForAssets(accessionAssets);
  sounds.playSound('ascension');

  StateObserver.dispatch(careActions.setCareAction({ type: 'ascension' }));

  await statePromise(
    ({ pet }) => pet[platform.playerID].action === 'ascension',
  );

  await waitForItPromise(1000);

  // Show white screen animation
  const hideWhiteScreenAnimation = await getSceneManager().showWhiteScreenEffect(
    2000,
  );

  // Pause a bit here
  await waitForItPromise(1000);

  // Deliver new pet
  const reborn = rebornSequence();

  // Hide white screen animation
  await hideWhiteScreenAnimation();

  // Block on reborn sequence
  await reborn;
}

/**
 * Deliver new pet after death
 */
export async function rebornSequence() {
  let pet = getPet();

  // Freeze pet
  pet.appearance.setAction(null);
  pet.appearance.setSpecies(null);

  // Hide pet movie clip
  pet.hide();

  // Load pet assets
  await waitForAssets(petCrateAssets);

  // Stop all pet animations
  const { height } = StateObserver.getState().ui.screenSize;
  pet.position.setWandering(false);

  // Hide pet information
  StateObserver.dispatch(setPetCrateOpening(true));

  // Create crate animation
  const petCrate = new MovieClip({
    superview: getSceneManager().scenes[getCurrentScene()].getView(),
    x: 365,
    y: height * 0.618,
    centerOnOrigin: true,
    zIndex: 1,
    url: 'assets/tutorial/crate',
    defaultAnimation: 'pet_crate_idle',
    offsetX: 170,
    offsetY: 124,
  });

  // Show dialog
  await waitForItPromise(500);
  await openPopupPromise('popupAcceptToS', { showTos: false });

  // Remember previous pet evolution
  const previousEvolution = Pet.getters(
    StateObserver.getState().user,
  ).getCurrentEvolution();

  // Decline all duo invitations.
  await StateObserver.invoke.cancelAllDuoInvitations();

  // Create new pet
  await StateObserver.invoke.rebornPet();
  // Analytics
  trackPetDie();

  // Load assets for species
  const { user } = StateObserver.getState();
  const { species } = Pet.getters(user).getCurrentEvolution();
  await withLoading(() => waitForAssets(petSpeciesAssets[species]));

  // Play open crate animation
  petCrate.play('pet_crate_open', () => {
    petCrate.removeFromSuperview();
  });

  // Show pet during crate opening animation
  await waitForItPromise(1250);

  // Set species skin
  pet.appearance.setSpecies(species);

  // Show pet movie clip
  pet.show();

  // Set pet positions
  void pet.moveTo({
    scene: 'livingRoom',
    location: 'c',
  });

  // Show greetings messages
  const evolution = Pet.ruleset.evolutions[species];
  const message = evolution[`greetings_${getCreativeSuffix()}`];
  await openPopupPromise('popupSpeechBubble', { message });

  // Ask new name
  const currentName = Pet.getters(user).getPetName();
  const name = await openPopupPromise('popupPetNameInput', {
    name: currentName,
  });

  // Update actual name
  await StateObserver.invoke.updateName(name);

  // Update name
  pet.appearance.setAttributes({ name });

  // Show new pet information
  StateObserver.dispatch(setPetCrateOpening(false));

  // Allow walk
  pet.position.setWandering(true);
  StateObserver.dispatch(careActions.setCareAction(null));

  // Schedule activity
  await StateObserver.invoke.schedulePetActivity();

  // Spawn analytics
  trackPetSpawn({
    feature: 'death',
    subFeature: `death_${previousEvolution.stage}`,
  });
}
